import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import usePrevious from '../../hooks/usePrevious';
import equals from 'ramda/src/equals';
import { DefaultChartStyles, formatKwValues, isDefined, KW_UNIT, } from '../../utils';
import { ComposedChart, CartesianGrid, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, ReferenceArea, ReferenceLine, ReferenceDot, } from 'recharts';
import ChartCommentDot from '../ChartCommentDot';
import ChartDot from '../ChartDot';
import SensorLineChartToolTip from '../SensorLineChartToolTip';
import ChartDateTickFormat from '../ChartDateTickFormat';
import Loader from '../Loader';
import ChartDisplayCheckboxes from '../ChartDisplayCheckboxes';
import SensorLineChartLegend from '../SensorLineChartLegend';
import styles from './EnergyLineChart.module.css';
import { useGraphTouchEvents } from '../../hooks';
import { useRefArea } from '../../hooks/useChartZoomUtils';
export var EnergyLineChartType;
(function (EnergyLineChartType) {
    EnergyLineChartType["POWER"] = "Power";
    EnergyLineChartType["CURRENT"] = "Current";
})(EnergyLineChartType || (EnergyLineChartType = {}));
const EnergyLineChart = React.forwardRef((props, ref) => {
    const { isMobile = false, isReport, isLoading, syncId, data, notifications, thresholdLines, locale, ianaTimeZoneCode, fromDate, toDate, ticks, 
    // refAreaStart,
    // refAreaEnd,
    zoomIn, zoom = (zoomRange) => { }, updateRefAreaStart, updateRefAreaEnd, 
    // startZoom,
    // onTouchStart,
    // onTouchMove,
    // onTouchEnd,
    isCommentModeActive, comments, newCommentReferenceDot, isNewCommentPopupOpen, onClickDuringCommentMode, hideCommentsCheckbox, hideThresholdLinesCheckbox, hideNotificationsCheckbox, type = EnergyLineChartType.POWER, displayCurrentInLegend, isCompressedHeight = false, } = props;
    const [showThresholdLines, setShowThresholdLines] = useState(isMobile);
    const [showNotifications, setShowNotifications] = useState(isMobile);
    const [showComments, setShowComments] = useState(isMobile);
    const [isInitialNotificationsLoadingFinished, setIsInitialNotificationsLoadingFinished,] = useState(false);
    const [isInitialCommentsLoadingFinished, setIsInitialCommentsLoadingFinished,] = useState(false);
    // // refArea should stay in child component to avoid too much rerender on parent component
    // const [refAreaLeft, setRefAreaLeft] = useState<string>('');
    // const [refAreaRight, setRefAreaRight] = useState<string>('');
    // useImperativeHandle(
    //   ref,
    //   () => ({
    //     refAreaLeft,
    //     refAreaRight,
    //   }),
    //   [refAreaLeft, refAreaRight]
    // );
    const previousComments = usePrevious(comments);
    const previousNotifications = usePrevious(notifications);
    const doNotificationsExist = notifications && Object.keys(notifications).length > 0;
    const doCommentsExist = comments && comments.length > 0;
    const isAbleToZoom = !isCommentModeActive;
    const isShowingPower = type === EnergyLineChartType.POWER;
    const chartLineColor = isShowingPower ? '#2ADDD0' : '#626DF9';
    const chartDataKey = isShowingPower ? 'value' : 'current';
    // refAreaStart and refAreaEnd should not stay at parent component to avoid too many rerender
    const { refAreaRange, setRefAreaFrom, setRefAreaTo, updateZoomRange } = useRefArea(zoom);
    const { from: refAreaStart, to: refAreaEnd } = refAreaRange;
    const { startZoom, onTouchStart, onTouchMove, onTouchEnd } = useGraphTouchEvents(isAbleToZoom, setRefAreaFrom, setRefAreaTo, updateZoomRange, true);
    // const {startZoom,
    //   onTouchStart,
    //   onTouchMove,
    //   onTouchEnd} = useGraphTouchEvents(isAbleToZoom, setRefAreaStart, setRefAreaEnd, () => zoom?.({from: refAreaStart, to: refAreaEnd}) ?? (() => {}));
    // TODO: remove props refAreaStart and refAreaEnd
    useEffect(() => {
        if (!isDefined(props.refAreaStart))
            return;
        setRefAreaFrom(Number(props.refAreaStart));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refAreaStart]);
    useEffect(() => {
        if (!isDefined(props.refAreaEnd))
            return;
        setRefAreaTo(Number(props.refAreaEnd));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refAreaEnd]);
    /** UseEffect **/
    useEffect(() => {
        if (!equals(previousNotifications, notifications) &&
            doNotificationsExist &&
            !isInitialNotificationsLoadingFinished) {
            setShowNotifications(true);
            setIsInitialNotificationsLoadingFinished(true);
        }
    }, [
        previousNotifications,
        notifications,
        doNotificationsExist,
        isInitialNotificationsLoadingFinished,
    ]);
    useEffect(() => {
        if (!equals(previousComments, comments) &&
            doCommentsExist &&
            !isInitialCommentsLoadingFinished) {
            setShowComments(true);
            setIsInitialCommentsLoadingFinished(true);
        }
    }, [
        previousComments,
        comments,
        doCommentsExist,
        isInitialCommentsLoadingFinished,
    ]);
    useEffect(() => {
        if (isCommentModeActive) {
            setShowNotifications(false);
        }
    }, [isCommentModeActive, doNotificationsExist]);
    /** End UseEffect **/
    if (!isLoading && !data) {
        return _jsx("div", { children: "Something went wrong. Please try again later." }, void 0);
    }
    const onMouseDown = (e) => {
        if (!isAbleToZoom)
            return;
        // TODO: remove props onTouchStart and use useGraphTouchEvent
        if (props.onTouchStart) {
            props.onTouchStart(e);
        }
        else if (e && e.activeLabel && updateRefAreaStart) {
            updateRefAreaStart(e.activeLabel);
        }
        else if (e) {
            onTouchStart(e);
        }
    };
    const onMouseMove = (e) => {
        if (!isAbleToZoom)
            return;
        // TODO: remove props onTouchMove and use useGraphTouchEvent
        if (props.onTouchMove) {
            props.onTouchMove(e);
        }
        else if (e && e.activeLabel && refAreaStart && updateRefAreaEnd) {
            updateRefAreaEnd(e.activeLabel);
        }
        else if (e) {
            onTouchMove(e);
        }
    };
    const onMouseUp = () => {
        if (!isAbleToZoom)
            return;
        // TODO: remove props onTouchEnd and use useGraphTouchEvent
        if (props.onTouchEnd) {
            props.onTouchEnd();
        }
        else if (zoomIn) {
            zoomIn();
        }
        else {
            onTouchEnd();
        }
    };
    return (_jsxs("div", Object.assign({ className: styles.root }, { children: [isLoading && (_jsx("div", Object.assign({ className: styles.loading }, { children: _jsx(Loader, {}, void 0) }), void 0)), !isLoading && (_jsx("div", Object.assign({ className: isReport ? styles.reportYAxisLabel : styles.yAxisLabel }, { children: isShowingPower ? KW_UNIT : 'A' }), void 0)), _jsx(ResponsiveContainer, Object.assign({ width: "100%", height: isCompressedHeight
                    ? DefaultChartStyles.chartCompressedHeight
                    : DefaultChartStyles.chartHeight }, { children: _jsxs(ComposedChart, Object.assign({ syncId: syncId, onClick: onClickDuringCommentMode, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp }, { children: [_jsx(CartesianGrid, { vertical: false, stroke: DefaultChartStyles.axisLineColor }, void 0), _jsx("defs", { children: _jsxs("linearGradient", Object.assign({ id: "gradient", x1: "0", y1: "0", x2: "0", y2: "1" }, { children: [_jsx("stop", { offset: "5%", stopColor: chartLineColor, stopOpacity: 0.4 }, void 0), _jsx("stop", { offset: "70%", stopColor: chartLineColor, stopOpacity: 0 }, void 0)] }), void 0) }, void 0), _jsx(Area, { data: data, type: "monotone", fill: "url(#gradient)", dataKey: chartDataKey, yAxisId: "yAxis", isAnimationActive: false, activeDot: isCommentModeActive }, void 0), _jsx(XAxis, { allowDataOverflow: true, axisLine: false, tickLine: false, dataKey: "timestamp", type: "number", domain: [fromDate, toDate], interval: 0, minTickGap: 0, ticks: ticks, tick: ({ x, y, payload }) => {
                                return (_jsx(ChartDateTickFormat, { x: x, y: y, payload: Object.assign(Object.assign({}, payload), { fromDate,
                                        toDate }), locale: locale, ianaTimeZoneCode: ianaTimeZoneCode, isReport: isReport }, void 0));
                            } }, void 0), _jsx(YAxis, { dataKey: (data) => {
                                const chartData = data[chartDataKey];
                                if (chartData !== undefined)
                                    return chartData;
                                return data['notificationValue'];
                            }, type: "number", width: DefaultChartStyles.yAxisWidth, axisLine: false, tickLine: false, yAxisId: "yAxis", tick: Object.assign(Object.assign({}, DefaultChartStyles.tick), { fontSize: isReport
                                    ? DefaultChartStyles.tick.reportFontSize
                                    : DefaultChartStyles.tick.fontSize }) }, void 0), _jsx(Line, { data: data, formatter: (v) => {
                                return isShowingPower
                                    ? formatKwValues(Number(v))
                                    : `${Number(v).toFixed(2)} A`;
                            }, type: "monotone", yAxisId: "yAxis", dataKey: chartDataKey, dot: false, stroke: chartLineColor, isAnimationActive: false, activeDot: isCommentModeActive }, void 0), !isLoading &&
                            notifications &&
                            showNotifications &&
                            Object.keys(notifications).length > 0 &&
                            Object.keys(notifications)
                                .filter((sensorType) => sensorType === 'ENERGY')
                                .map((notificationType) => (_jsx(Line, { activeDot: false, yAxisId: "yAxis", dataKey: "notificationValue", 
                                //need this bc sentinelsAPI erroneously sends energy notifications
                                //with kW values less than 0
                                data: notifications[notificationType].filter((n) => n.notificationValue >= 0), name: `Notifications-${notificationType}`, formatter: (v) => v, strokeWidth: 0, legendType: "none", isAnimationActive: false, dot: _jsx(ChartDot, {}, void 0) }, notificationType))), showThresholdLines && thresholdLines.length > 0
                            ? thresholdLines
                                .filter((t) => t.type !== undefined)
                                .map((t) => (_jsx(ReferenceLine, { y: t.value, yAxisId: "yAxis", stroke: "red", strokeDasharray: "3 3" }, t.title)))
                            : null, showComments && (comments || []).length > 0 && (_jsx(Line, { activeDot: false, yAxisId: "yAxis", dataKey: isShowingPower ? 'value' : 'current', name: 'Comments-line', data: comments, strokeWidth: 0, legendType: "none", isAnimationActive: false, dot: ({ cx, cy }) => _jsx(ChartCommentDot, { cx: cx, cy: cy }, void 0), formatter: (v) => formatKwValues(Number(v)) }, void 0)), !isNewCommentPopupOpen && (_jsx(Tooltip, { content: startZoom ? (_jsx("div", {}, void 0)) : (({ payload, label }) => {
                                return (_jsx(SensorLineChartToolTip, { locale: locale, payload: payload, label: label, ianaTimeZoneCode: ianaTimeZoneCode }, void 0));
                            }), isAnimationActive: false }, void 0)), _jsx(Legend, { content: _jsx(SensorLineChartLegend, { isReport: isReport, primaryLabel: "Power", secondaryLabel: displayCurrentInLegend ? 'Current' : undefined, showNotifications: !hideNotificationsCheckbox, showComments: !hideCommentsCheckbox }, void 0), wrapperStyle: DefaultChartStyles.legendContainer }, void 0), refAreaStart && refAreaEnd ? (_jsx(ReferenceArea, { yAxisId: "yAxis", x1: refAreaStart, x2: refAreaEnd, strokeOpacity: 0.3 }, void 0)) : null, newCommentReferenceDot && (_jsx(ReferenceDot, { isFront: true, yAxisId: "yAxis", x: newCommentReferenceDot.x, y: newCommentReferenceDot.y, fill: "#0095ff", stroke: "#0095ff", r: 3 }, void 0))] }), void 0) }), void 0), !isMobile && (_jsx(ChartDisplayCheckboxes, { className: isCompressedHeight ? styles.margin0 : '', showComments: showComments, onClickCommentsCheckbox: () => setShowComments(!showComments), isCommentsCheckboxDisabled: !comments || comments.length === 0, hideCommentsCheckbox: hideCommentsCheckbox, showThresholdLines: showThresholdLines, onClickThresholdLinesCheckbox: () => setShowThresholdLines(!showThresholdLines), isThresholdLinesCheckboxDisabled: thresholdLines.length === 0, hideThresholdLinesCheckbox: hideThresholdLinesCheckbox, showNotifications: showNotifications, onClickNotificationsCheckbox: () => setShowNotifications(!showNotifications), isNotificationsCheckboxDisabled: !notifications ||
                    Object.keys(notifications).length === 0 ||
                    isCommentModeActive, hideNotificationsCheckbox: hideNotificationsCheckbox, hideControlDetailsCheckbox: true }, void 0))] }), void 0));
});
export default EnergyLineChart;
